import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUpRight, UserRound, Target, RotateCw, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import PageHeader from "@/components/PageHeader";
import BulkImportLearners from "@/components/BulkImportLearners";
import { LEARNERS } from "@/constants/testIds";

const emptyForm = { chart_name: "", notes: "" };

export default function LearnersPage() {
  const [learners, setLearners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const load = () =>
    http.get("/learners").then((r) => setLearners(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await http.post("/learners", form);
      setOpen(false);
      setForm(emptyForm);
      toast.success("Learner added");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this learner and all assignments?")) return;
    try {
      await http.delete(`/learners/${id}`);
      toast.success("Learner removed");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="p-4 sm:p-8 md:p-12 max-w-6xl">
      <PageHeader
        eyebrow="Roster"
        title="Learners"
        description="Use a chart name or initials only — real profile & billing details live in your practice management system."
        actions={
          <div className="flex gap-2 flex-wrap">
            <BulkImportLearners onDone={load} />
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(emptyForm); }}>
            <DialogTrigger asChild>
              <Button data-testid={LEARNERS.addBtn} className="rounded-full h-11 px-5">
                <Plus className="w-4 h-4 mr-2" /> Add learner
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display">New learner</DialogTitle>
                <DialogDescription>
                  Add a chart name (e.g. initials, "Learner A") — do not enter full name, DOB, or PHI.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={save} className="space-y-4">
                <div>
                  <Label htmlFor="cn">Chart name / ID</Label>
                  <Input id="cn" data-testid={LEARNERS.nameInput} required autoFocus
                    value={form.chart_name}
                    onChange={(e) => setForm({ ...form, chart_name: e.target.value })}
                    placeholder='e.g. "JS" or "Learner A"'
                    maxLength={80}
                    className="mt-1.5 rounded-xl" />
                  <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> No PHI — keep it de-identified.
                  </div>
                </div>
                <div>
                  <Label htmlFor="notes">BCBA reminders <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <Textarea id="notes" data-testid={LEARNERS.notesInput} rows={3}
                    value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="mt-1.5 rounded-xl"
                    placeholder="Short reminders about programming, prompts, or reinforcement — no identifying info." />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid={LEARNERS.saveBtn} className="rounded-full">
                    {saving ? "Saving…" : "Save learner"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      {learners.length === 0 ? (
        <EmptyState onAdd={() => setOpen(true)} />
      ) : (
        <>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {learners.map((l) => (
            <div
              key={l.id}
              data-testid={LEARNERS.card(l.id)}
              className="group bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 card-hover"
            >
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                  <UserRound className="w-5 h-5" />
                </div>
                <button
                  data-testid={LEARNERS.deleteBtn(l.id)}
                  onClick={() => remove(l.id)}
                  className="md:opacity-0 md:group-hover:opacity-100 transition-opacity text-slate-400 hover:text-rose-600"
                  aria-label="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-4 font-display font-bold text-xl text-slate-900">{l.chart_name}</div>
              <div className="mt-1 text-xs uppercase tracking-wider text-slate-400">Chart name</div>

              {/* Target / Maintenance count chips */}
              <div className="mt-4 flex items-center gap-2">
                <span
                  data-testid={`learner-target-count-${l.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                >
                  <Target className="w-3 h-3" /> {l.target_count ?? 0} Targets
                </span>
                <span
                  data-testid={`learner-maint-count-${l.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
                >
                  <RotateCw className="w-3 h-3" /> {l.maintenance_count ?? 0} Maint.
                </span>
              </div>

              {l.notes && (
                <p className="mt-3 text-sm text-slate-600 line-clamp-2">{l.notes}</p>
              )}
              <button
                data-testid={LEARNERS.openBtn(l.id)}
                onClick={() => nav(`/learners/${l.id}`)}
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-900 hover:text-blue-600"
              >
                Open profile <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="border-2 border-dashed border-slate-200 rounded-2xl p-10 sm:p-16 text-center bg-white">
      <div className="mx-auto w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
        <UserRound className="w-6 h-6 text-slate-500" />
      </div>
      <h3 className="mt-4 font-display font-bold text-xl">No learners yet</h3>
      <p className="text-slate-500 mt-2 text-sm">Add your first learner (chart name only) to start assigning targets and maintenance.</p>
      <Button onClick={onAdd} className="rounded-full mt-6" data-testid={LEARNERS.addBtn + "-empty"}>
        <Plus className="w-4 h-4 mr-2" /> Add learner
      </Button>
    </div>
  );
}
